import type { StreamSlot } from "../types/stream";

/** The production hosted player; builds without VITE_LINK_PLAYER_SCRIPT load this one. */
export const PROD_LINK_PLAYER_SCRIPT =
  "https://player.streammapp.com/v1/player.js";

/** The build's player script: VITE_LINK_PLAYER_SCRIPT when set (staging has its own copy), else prod. */
export function linkPlayerScriptOf(configured: string | undefined): string {
  const v = configured?.trim();
  return v ? v : PROD_LINK_PLAYER_SCRIPT;
}

/** The hosted YouTube + SoundCloud player (web_streammapp_com src/player/v1). */
export const LINK_PLAYER_SCRIPT = linkPlayerScriptOf(
  import.meta.env.VITE_LINK_PLAYER_SCRIPT,
);

export type LinkPlayerEvent =
  | { type: "loaded"; provider: string; durationMs?: number; v: number }
  | {
      type: "state";
      state: "playing" | "paused" | "buffering" | "ended";
      positionMs: number;
      v: number;
    }
  | { type: "position"; positionMs: number; v: number }
  | { type: "error"; code: string; detail?: string; v: number };

export interface LinkPlayerController {
  /** `controls`: the provider's own controls, taps reach it (YouTube only; hosted players with CONTROLS). */
  load(url: string, opts: { startMs: number; play: boolean; controls?: boolean }): void;
  play(): void;
  pause(): void;
  seekTo(ms: number): void;
  destroy(): void;
}

export interface LinkPlayerApi {
  /** load() takes `controls` (hosted player b6718f0+); older ones ignore it and stay shielded. */
  CONTROLS?: boolean;
  mount(
    el: HTMLElement,
    onEvent: (e: LinkPlayerEvent) => void,
  ): LinkPlayerController;
}

let loading: Promise<LinkPlayerApi> | null = null;

/** Loads the player script once; a failed load is retried by the next call. */
export function loadLinkPlayer(
  src: string = LINK_PLAYER_SCRIPT,
): Promise<LinkPlayerApi> {
  if (loading) return loading;
  loading = new Promise<LinkPlayerApi>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => {
      const api = (window as unknown as { IeupPlayer?: LinkPlayerApi })
        .IeupPlayer;
      if (api && typeof api.mount === "function") resolve(api);
      else {
        loading = null;
        reject(new Error("link player script has no IeupPlayer"));
      }
    };
    s.onerror = () => {
      loading = null;
      reject(new Error("link player script failed to load"));
    };
    document.head.appendChild(s);
  });
  return loading;
}

export function resetLinkPlayerForTests() {
  loading = null;
}

/** What to play for a link slot; old YouTube casts may only carry the video id. */
export function linkUrlOf(slot: StreamSlot): string | null {
  if (slot.mediaUrl) return slot.mediaUrl;
  if (slot.youtubeVideoId)
    return `https://www.youtube.com/watch?v=${slot.youtubeVideoId}`;
  return null;
}
